import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const WorkflowPage = () => {
  const [activeTab, setActiveTab] = useState("tradepro"); // "tradepro" or "streetfolio"

  // Tab-specific states
  const [tradeproData, setTradeproData] = useState({
    leadCreationList: [],
    loading: false,
    error: null,
    workflowNameFilter: "",
    leadSourceFilter: "",
    languageFilter: "",
    allottedUserFilter: "",
    page: 1,
    pageCount: 1,
    total: 0
  });

  const [streetfolioData, setStreetfolioData] = useState({
    leadCreationList: [],
    loading: false,
    error: null,
    workflowNameFilter: "",
    leadSourceFilter: "",
    languageFilter: "",
    allottedUserFilter: "",
    page: 1,
    pageCount: 1,
    total: 0
  });

  const limit = 100;
  const token = localStorage.getItem("authToken");

  // Fetch Tradepro data
  const fetchTradeproLeads = async () => {
    try {
      setTradeproData(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}leadCreation`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setTradeproData(prev => ({
        ...prev,
        leadCreationList: response.data.data.data,
        total: response.data.data.data.length,
        pageCount: Math.ceil(response.data.data.data.length / limit),
        loading: false
      }));
    } catch (error) {
      setTradeproData(prev => ({
        ...prev,
        error: error.message,
        loading: false
      }));
    }
  };

  // Fetch Streetfolio data
  const fetchStreetfolioLeads = async () => {
    try {
      setStreetfolioData(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}leadCreationstreet`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setStreetfolioData(prev => ({
        ...prev,
        leadCreationList: response.data.data.data,
        total: response.data.data.data.length,
        pageCount: Math.ceil(response.data.data.data.length / limit),
        loading: false
      }));
    } catch (error) {
      setStreetfolioData(prev => ({
        ...prev,
        error: error.message,
        loading: false
      }));
    }
  };

  useEffect(() => {
    fetchTradeproLeads();
    fetchStreetfolioLeads();
  }, []);

  // Filter functions for both tabs
  const filteredTradeproWorkflows = useMemo(() => {
    return tradeproData.leadCreationList.filter((workflow) => {
      if (
        tradeproData.workflowNameFilter &&
        !workflow.workflow_name
          .toLowerCase()
          .includes(tradeproData.workflowNameFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        tradeproData.leadSourceFilter &&
        !workflow.lead_source
          .toLowerCase()
          .includes(tradeproData.leadSourceFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        tradeproData.languageFilter &&
        !(workflow.languages || workflow.language || "")
          .toLowerCase()
          .includes(tradeproData.languageFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        tradeproData.allottedUserFilter &&
        !(workflow.allot_to_user_fullname || "")
          .toLowerCase()
          .includes(tradeproData.allottedUserFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [tradeproData.leadCreationList, tradeproData.workflowNameFilter, tradeproData.leadSourceFilter, tradeproData.languageFilter, tradeproData.allottedUserFilter]);

  const filteredStreetfolioWorkflows = useMemo(() => {
    return streetfolioData.leadCreationList.filter((workflow) => {
      if (
        streetfolioData.workflowNameFilter &&
        !workflow.workflow_name
          .toLowerCase()
          .includes(streetfolioData.workflowNameFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        streetfolioData.leadSourceFilter &&
        !workflow.lead_source
          .toLowerCase()
          .includes(streetfolioData.leadSourceFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        streetfolioData.languageFilter &&
        !(workflow.languages || workflow.language || "")
          .toLowerCase()
          .includes(streetfolioData.languageFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        streetfolioData.allottedUserFilter &&
        !(workflow.allot_to_user_fullname || "")
          .toLowerCase()
          .includes(streetfolioData.allottedUserFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [streetfolioData.leadCreationList, streetfolioData.workflowNameFilter, streetfolioData.leadSourceFilter, streetfolioData.languageFilter, streetfolioData.allottedUserFilter]);

  // Update pagination when filters change
  useEffect(() => {
    setTradeproData(prev => ({
      ...prev,
      total: filteredTradeproWorkflows.length,
      pageCount: Math.ceil(filteredTradeproWorkflows.length / limit),
      page: 1
    }));
  }, [filteredTradeproWorkflows, limit]);

  useEffect(() => {
    setStreetfolioData(prev => ({
      ...prev,
      total: filteredStreetfolioWorkflows.length,
      pageCount: Math.ceil(filteredStreetfolioWorkflows.length / limit),
      page: 1
    }));
  }, [filteredStreetfolioWorkflows, limit]);

  // Pagination handlers
  const handleTradeproPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= tradeproData.pageCount) {
      setTradeproData(prev => ({ ...prev, page: newPage }));
    }
  };

  useEffect(() => {
    if (activeTab === "tradepro") {
      setTradeproData(prev => ({ ...prev, loading: true }));
      setTimeout(() => {
        setTradeproData(prev => ({ ...prev, loading: false }));
      }, 300);
    }

    if (activeTab === "streetfolio") {
      setStreetfolioData(prev => ({ ...prev, loading: true }));
      setTimeout(() => {
        setStreetfolioData(prev => ({ ...prev, loading: false }));
      }, 300);
    }
  }, [activeTab]);


  const handleStreetfolioPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= streetfolioData.pageCount) {
      setStreetfolioData(prev => ({ ...prev, page: newPage }));
    }
  };

  // Delete handlers
  const handleTradeproDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}leadCreation/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setTradeproData(prev => ({
          ...prev,
          leadCreationList: prev.leadCreationList.filter((dt) => dt.id !== id)
        }));

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  const handleStreetfolioDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}leadCreationstreet/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setStreetfolioData(prev => ({
          ...prev,
          leadCreationList: prev.leadCreationList.filter((dt) => dt.id !== id)
        }));

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  // Helper functions to get paginated data
  const getTradeproPaginatedData = () => {
    const start = (tradeproData.page - 1) * limit + 1;
    const end = Math.min(tradeproData.page * limit, tradeproData.total);
    const paginatedData = filteredTradeproWorkflows.slice(start - 1, end);
    return { start, end, paginatedData };
  };

  const getStreetfolioPaginatedData = () => {
    const start = (streetfolioData.page - 1) * limit + 1;
    const end = Math.min(streetfolioData.page * limit, streetfolioData.total);
    const paginatedData = filteredStreetfolioWorkflows.slice(start - 1, end);
    return { start, end, paginatedData };
  };

  const { start: tradeproStart, end: tradeproEnd, paginatedData: tradeproPaginated } = getTradeproPaginatedData();
  const { start: streetfolioStart, end: streetfolioEnd, paginatedData: streetfolioPaginated } = getStreetfolioPaginatedData();

  // Update filter handlers
  const updateTradeproFilter = (field, value) => {
    setTradeproData(prev => ({
      ...prev,
      loading: true,      // ✅ trigger skeleton
      [field]: value
    }));

    setTimeout(() => {
      setTradeproData(prev => ({ ...prev, loading: false }));
    }, 300);
  };

  const updateStreetfolioFilter = (field, value) => {
    setStreetfolioData(prev => ({
      ...prev,
      loading: true,      // ✅ ADD
      [field]: value
    }));

    setTimeout(() => {
      setStreetfolioData(prev => ({ ...prev, loading: false }));
    }, 300);
  };

  const clearTradeproFilters = () => {
    setTradeproData(prev => ({
      ...prev,
      workflowNameFilter: "",
      leadSourceFilter: "",
      languageFilter: "",
      allottedUserFilter: ""
    }));
  };

  const clearStreetfolioFilters = () => {
    setStreetfolioData(prev => ({
      ...prev,
      workflowNameFilter: "",
      leadSourceFilter: "",
      languageFilter: "",
      allottedUserFilter: ""
    }));
  };


  useEffect(() => {
    const handleClearFilters = () => {
      clearTradeproFilters();
      clearStreetfolioFilters();
    };
    window.addEventListener('clearLeadCreationFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearLeadCreationFilterEvent', handleClearFilters);
    };
  }, []);


  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* {(tradeproData.loading || streetfolioData.loading) && <LoadingSpinner />} */}
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0">
            <div className="row">
              <div className="col-md-6">
                <h4 className="panel-title pull-left">Workflow Lead Creation</h4>
              </div>
              <div className="col-md-6" style={{ textAlign: "right" }}>
                <PermissionWrapper module="workflow_lead" action="add">
                  {activeTab === "tradepro" && (
                    <Link
                      to="/WorkflowTradepro/LeadCreation/add/"
                      className="btn btn-default btn-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Tradepro Workflow
                    </Link>
                  )}
                  {activeTab === "streetfolio" && (
                    <Link
                      to="/WorkflowStreetfolio/LeadCreation/add/"
                      className="btn btn-default btn-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Streetfolio Workflow
                    </Link>
                  )}
                </PermissionWrapper>
                {/* Pagination Controls - Show based on active tab */}
                {activeTab === "tradepro" && tradeproData.leadCreationList.length > 0 && (
                  <div className="d-inline-block ms-2">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {tradeproStart} to {tradeproEnd} of {tradeproData.total}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handleTradeproPageChange(tradeproData.page - 1)}
                      disabled={tradeproData.page === 1}
                    >
                      &lt;
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handleTradeproPageChange(tradeproData.page + 1)}
                      disabled={tradeproData.page === tradeproData.pageCount}
                    >
                      &gt;
                    </button>
                  </div>
                )}
                {activeTab === "streetfolio" && streetfolioData.leadCreationList.length > 0 && (
                  <div className="d-inline-block">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {streetfolioStart} to {streetfolioEnd} of {streetfolioData.total}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handleStreetfolioPageChange(streetfolioData.page - 1)}
                      disabled={streetfolioData.page === 1}
                    >
                      &lt;
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handleStreetfolioPageChange(streetfolioData.page + 1)}
                      disabled={streetfolioData.page === streetfolioData.pageCount}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation - Same style as first example */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <label className="me-3">Select Workflow Type:</label>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${activeTab === "tradepro" ? "btn-primary" : "btn-default"}`}
                      onClick={() => setActiveTab("tradepro")}
                    >
                      Tradepro Workflows
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === "streetfolio" ? "btn-primary" : "btn-default"}`}
                      onClick={() => setActiveTab("streetfolio")}
                    >
                      Streetfolio Workflows
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tradepro Tab Content */}
          {activeTab === "tradepro" && (
            <>
              {tradeproData.leadCreationList.length > 0 || tradeproData.loading ? (
                <div className="table-responsive sticky-table-container">
                  <style>
                    {`
                      .sticky-seo-table thead tr:first-child th {
                        position: sticky;
                        top: 0;
                        z-index: 20;
                        background: #f8f9fa;
                       }

                      .sticky-seo-table thead tr:nth-child(2) th {
                        position: sticky;
                        top: 25px; /* Adjust if needed based on first header height */
                        z-index: 19;
                        background: white;
                       }

                      .sticky-table-container {
                        max-height: calc(100vh - 268px); /* Adjust height if needed */
                        overflow: auto;
                       }

                      .sticky-seo-table th {
                        vertical-align: top;
                       }
                    `}
                  </style>
                  <table className="table table-condensed table-bordered bg-white sticky-seo-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center" }}> #</th>
                        <th>Workflow Name</th>
                        <th>Lead Source</th>
                        <th>Language</th>
                        <th style={{ maxWidth: "40%", whiteSpace: "normal", wordBreak: "break-word" }}>Allotted User</th>
                        <th style={{ width: "1%" }}>Edit</th>
                        <th style={{ width: "1%" }}>Delete</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: "center" }}></th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search workflow..."
                            value={tradeproData.workflowNameFilter}
                            onChange={(e) => updateTradeproFilter("workflowNameFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search source..."
                            value={tradeproData.leadSourceFilter}
                            onChange={(e) => updateTradeproFilter("leadSourceFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search language..."
                            value={tradeproData.languageFilter}
                            onChange={(e) => updateTradeproFilter("languageFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search user..."
                            value={tradeproData.allottedUserFilter}
                            onChange={(e) => updateTradeproFilter("allottedUserFilter", e.target.value)}
                          />
                        </th>
                        <th colSpan="2">
                          <button
                            className="btn btn-default btn-sm"
                            onClick={clearTradeproFilters}
                            title="Clear filters"
                          >
                            Clear
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeproData.loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                          <tr key={index} className="skeleton-row">
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                          </tr>
                        ))
                      ) : tradeproPaginated.length > 0 ? (
                        tradeproPaginated.map((post, index) => (
                          <tr key={post.id}>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              {(tradeproData.page - 1) * limit + index + 1}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>{post.workflow_name}</td>
                            <td style={{ verticalAlign: "middle" }}>{post.lead_source}</td>
                            <td style={{ verticalAlign: "middle" }}>{[post.languages, post.language].find(val => val && val !== "0" && val !== 0) || ""}</td>
                            <td style={{ verticalAlign: "middle", maxWidth: "40%", whiteSpace: "normal", wordBreak: "break-word" }}>{post.allot_to_user_fullname}</td>
                            <td>
                              <PermissionWrapper module="workflow_lead" action="edit">
                                <Link
                                  to={`/WorkflowTradepro/LeadCreation/edit/${post.id}`}
                                  className="btn btn-warning btn-xs"
                                  style={{ float: "left" }}
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </Link>
                              </PermissionWrapper>
                            </td>
                            <td>
                              <PermissionWrapper module="workflow_lead" action="delete">
                                <button
                                  className="cancel btn btn-danger btn-xs"
                                  style={{ float: "left" }}
                                  onClick={() => handleTradeproDelete(post.id)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </PermissionWrapper>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No Tradepro workflows match your search criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>

                  </table>
                </div>
              ) : (
                <div className="p-3">
                  No Tradepro records found.{" "}
                  <Link to="/WorkflowTradepro/LeadCreation/add/">Add a new workflow</Link>
                </div>
              )}
            </>
          )}

          {/* Streetfolio Tab Content */}
          {activeTab === "streetfolio" && (
            <>
              {streetfolioData.leadCreationList.length > 0 || streetfolioData.loading ? (
                <div className="table-responsive sticky-table-container">
                  <style>
                    {`
                      .sticky-seo-table thead tr:first-child th {
                        position: sticky;
                        top: 0;
                        z-index: 20;
                        background: #f8f9fa;
                       }

                      .sticky-seo-table thead tr:nth-child(2) th {
                        position: sticky;
                        top: 25px; /* Adjust if needed based on first header height */
                        z-index: 19;
                        background: white;
                       }

                      .sticky-table-container {
                        max-height: calc(100vh - 268px); /* Adjust height if needed */
                        overflow: auto;
                       }

                      .sticky-seo-table th {
                        vertical-align: top;
                       }
                    `}
                  </style>
                  <table className="table table-condensed table-bordered bg-white sticky-seo-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center" }}>#</th>
                        <th>Workflow Name</th>
                        <th>Lead Source</th>
                        <th>Language</th>
                        <th style={{ maxWidth: "40%", whiteSpace: "normal", wordBreak: "break-word" }}>Allotted User</th>
                        <th style={{ width: "1%" }}>Edit</th>
                        <th style={{ width: "1%" }}>Delete</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: "center" }}></th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search workflow..."
                            value={streetfolioData.workflowNameFilter}
                            onChange={(e) => updateStreetfolioFilter("workflowNameFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search source..."
                            value={streetfolioData.leadSourceFilter}
                            onChange={(e) => updateStreetfolioFilter("leadSourceFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search language..."
                            value={streetfolioData.languageFilter}
                            onChange={(e) => updateStreetfolioFilter("languageFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search user..."
                            value={streetfolioData.allottedUserFilter}
                            onChange={(e) => updateStreetfolioFilter("allottedUserFilter", e.target.value)}
                          />
                        </th>
                        <th colSpan="2">
                          <button
                            className="btn btn-default btn-sm"
                            onClick={clearStreetfolioFilters}
                            title="Clear filters"
                          >
                            Clear
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {streetfolioData.loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                          <tr key={index} className="skeleton-row">
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                          </tr>
                        ))
                      ) : streetfolioPaginated.length > 0 ? (
                        streetfolioPaginated.map((post, index) => (
                          <tr key={post.id}>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              {(streetfolioData.page - 1) * limit + index + 1}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>{post.workflow_name}</td>
                            <td style={{ verticalAlign: "middle" }}>{post.lead_source}</td>
                            <td style={{ verticalAlign: "middle" }}>{[post.languages, post.language].find(val => val && val !== "0" && val !== 0) || ""}</td>
                            <td style={{ verticalAlign: "middle", maxWidth: "40%", whiteSpace: "normal", wordBreak: "break-word" }}>{post.allot_to_user_fullname}</td>
                            <td>
                              <PermissionWrapper module="workflow_lead" action="edit">
                                <Link
                                  to={`/WorkflowStreetfolio/LeadCreation/edit/${post.id}`}
                                  className="btn btn-warning btn-xs"
                                  style={{ float: "left" }}
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </Link>
                              </PermissionWrapper>
                            </td>
                            <td>
                              <PermissionWrapper module="workflow_lead" action="delete">
                                <button
                                  className="cancel btn btn-danger btn-xs"
                                  style={{ float: "left" }}
                                  onClick={() => handleStreetfolioDelete(post.id)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </PermissionWrapper>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No Streetfolio workflows match your search criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-3">
                  No Streetfolio records found.{" "}
                  <Link to="/WorkflowStreetfolio/LeadCreation/add/">Add a new workflow</Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Modal component */}
      <div id="myModal" className="modal">
        <div className="modal-dialog panel panel-default panel-border top">
          <div className="modal-content">
            <div id="myModalContent"></div>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default WorkflowPage;